<template>
    <div class="registration-section">
        <v-row>
            <v-col class="pb-0">
                <h3>Registration</h3>
            </v-col>
        </v-row>

        <!-- Info Banner -->
        <v-row>
            <v-col>
                <v-alert
                    type="info"
                    dense
                    text
                    class="mb-0"
                >
                    <small>Name, Email, and Phone can be updated in <router-link to="/account-settings">Account Settings</router-link></small>
                </v-alert>
            </v-col>
        </v-row>

        <!-- Gender -->
        <v-row>
            <v-col cols="12" class="pb-0">
                <v-row>
                    <v-col class="pb-0">
                        <h5>
                            Gender
                            <span v-if="!user.gender" class="incomplete-badge">Required</span>
                        </h5>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pt-0">
                        <p class="extra-info">We use this for gender-specific roommate searches.</p>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pb-0" cols="12" md="4">
                        <v-btn
                            outlined
                            block
                            class="btn-edurent font-weight-regular"
                            height="45"
                            :class="user.gender === 'male' ? 'field-selected' : ''"
                            :color="user.gender === 'male' ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, gender: 'male' })"
                        >Male</v-btn>
                    </v-col>
                    <v-col class="pb-0" cols="12" md="4">
                        <v-btn
                            outlined
                            block
                            class="btn-edurent font-weight-regular"
                            height="45"
                            :class="user.gender === 'female' ? 'field-selected' : ''"
                            :color="user.gender === 'female' ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, gender: 'female' })"
                        >Female</v-btn>
                    </v-col>
                    <v-col class="pb-0" cols="12" md="4">
                        <v-btn
                            outlined
                            block
                            class="btn-edurent font-weight-regular"
                            height="45"
                            :class="user.gender === 'other' ? 'field-selected' : ''"
                            :color="user.gender === 'other' ? 'primary' : 'rgba(0,0,0,0.2)'"
                            @click="$emit('update:user', { ...user, gender: 'other' })"
                        >Prefer not to say</v-btn>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <!-- Date of Birth & Major/Program -->
        <v-row>
            <v-col cols="12" md="6" class="pb-0">
                <v-row>
                    <v-col class="pb-0">
                        <h5>
                            Date of Birth
                            <span v-if="!user.date_of_birth" class="incomplete-badge">Required</span>
                        </h5>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pb-0">
                        <v-menu
                            v-model="menu"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            max-width="290px"
                            min-width="290px"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    :value="computedDateFormatted"
                                    append-icon="mdi-calendar"
                                    solo
                                    flat
                                    outlined
                                    readonly
                                    v-on="on"
                                    class="btn-edu-select-height"
                                    hide-details="auto"
                                    placeholder="Select date"
                                >
                                    <template slot="append">
                                        <span>
                                            <v-icon size="18">mdi-calendar-blank-outline</v-icon>
                                        </span>
                                    </template>
                                </v-text-field>
                            </template>
                            <v-date-picker
                                no-title
                                @input="onDateInput"
                                :value="user.date_of_birth"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="6" class="pb-0">
                <v-row>
                    <v-col class="pb-0">
                        <h5>Major/Program <span class="optional-label">(Optional)</span></h5>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pb-0">
                        <v-text-field
                            outlined
                            dense
                            height="45px"
                            placeholder="Business, Finance, Welding, Art, etc."
                            class="font-weight-regular px-0"
                            hide-details="auto"
                            :value="user.program"
                            @input="$emit('update:user', { ...user, program: $event })"
                        ></v-text-field>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </div>
</template>

<script>
export default {
    name: 'RegistrationSection',
    props: {
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            menu: false
        }
    },
    computed: {
        computedDateFormatted() {
            return this.formatDate(this.user.date_of_birth)
        }
    },
    methods: {
        formatDate(date) {
            if (!date) return null
            const [year, month, day] = date.split('-')
            return `${month}/${day}/${year}`
        },
        onDateInput(value) {
            this.menu = false
            this.$emit('update:user', { ...this.user, date_of_birth: value })
        }
    }
}
</script>

<style scoped>
.registration-section h3 {
    font-size: 26px !important;
    font-weight: 700;
    color: #000;
}

.registration-section h5 {
    font-size: 14px !important;
    font-weight: 700;
    color: #000;
}

.registration-section .field-selected {
    background: rgba(45, 118, 227, 0.1);
}

.registration-section .extra-info {
    font-size: 12px !important;
    color: #000;
    font-style: italic;
}

.incomplete-badge {
    font-size: 10px;
    font-weight: 600;
    color: #FF9800;
    background: rgba(255, 152, 0, 0.1);
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 8px;
    vertical-align: middle;
}

.optional-label {
    font-size: 12px;
    font-weight: 400;
    color: #666;
}
</style>
